import {
  BooleanField,
  BooleanInput,
  ChipField,
  Create,
  Datagrid,
  DateField,
  Edit,
  EditButton,
  DeleteButton,
  Filter,
  FormDataConsumer,
  FunctionField,
  ImageField,
  List,
  NumberField,
  NumberInput,
  NullableBooleanInput,
  Pagination,
  ReferenceArrayField,
  ReferenceField,
  ReferenceInput,
  Resource,
  SelectArrayInput,
  SelectInput,
  Show,
  SimpleForm,
  SimpleShowLayout,
  SingleFieldList,
  TextField,
  TextInput,
  UrlField,
  useGetList,
  required,
  minValue,
  maxValue,
  number,
} from "react-admin";
import { MARKET_SEGMENT_CHOICES } from "../consts";

const ProjectPagination = (props) => (
  <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />
);

const validatePercentage = (value) => {
  if (value === null || value === undefined || value === '') return undefined;
  if (isNaN(value) || Number(value) < 0 || Number(value) > 100) return 'Must be a number between 0 and 100';
  return undefined;
};

const percentageFormat = (value) => {
  const parsed = parseFloat(value);
  return !isNaN(parsed) ? `${Math.round(parsed * 100)}%` : "—";
};

const YES_NO_CHOICES = [
  { id: 'YES', name: 'YES' },
  { id: 'NO', name: 'NO' },
  { id: 'NOT_DECLARED', name: 'NOT_DECLARED' },
];

// Visual section header used inside SimpleShowLayout / SimpleForm to group fields.
const SectionTitle = ({ children }) => (
  <div style={{
    marginTop: 16,
    marginBottom: 8,
    fontWeight: 600,
    fontSize: '1rem',
    borderBottom: '1px solid #eee',
    paddingBottom: 4,
    width: '100%',
  }}>
    {children}
  </div>
);

// Render care_instructions (array de códigos ISO 3758) como texto; vacío -> "—".
const renderCareInstructions = (record) =>
  Array.isArray(record.care_instructions) && record.care_instructions.length > 0
    ? record.care_instructions.join(", ")
    : "—";

// Selector múltiple de códigos ISO 3758. care_instructions guarda el iso_code
// (ej. "1B"), no el UUID, por lo que las choices usan iso_code como id.
const CareInstructionsInput = (props) => {
  const { data, ids, loading } = useGetList(
    'iso3758CareCodes',
    { page: 1, perPage: 1000 },
    { field: 'iso_code', order: 'ASC' },
    {}
  );

  const choices = (ids || [])
    .map((id) => data[id])
    .filter(Boolean)
    .map((rec) => ({
      id: rec.iso_code,
      name: rec.description ? `${rec.iso_code} - ${rec.description}` : rec.iso_code,
    }));

  return (
    <SelectArrayInput
      {...props}
      choices={choices}
      loading={loading}
    />
  );
};

// ─── Circularity inputs (IT-3175) ───────────────────────────────────────────────

// article_application names for which Colour Fastness / Pilling Resistance are N/A.
const CF_PR_NA_APPLICATIONS = ['Footwear', 'Eyewear', 'Bottles', 'Mattresses'];

const isNaApplication = (name) =>
  !!name && CF_PR_NA_APPLICATIONS.some((n) => n.toLowerCase() === String(name).toLowerCase());

// The 8 nullable-boolean circularity questions (used for the list summary).
const CIRCULARITY_BOOLEAN_FIELDS = [
  'dl_mat_3', 'dl_rep_1', 'dl_rep_2', 'dl_sty_1',
  'dl_sty_2', 'dl_mod_1', 'dl_mod_2', 'component_separability',
];

// Optional numeric fields that must persist as null (never "") on save.
const CIRCULARITY_NUMERIC_FIELDS = [
  'colour_fastness', 'pilling_resistance', 'declared_service_life', 'selling_price',
  'recovery_rate', 'eol_recycling', 'recycling_efficiency', 'eol_reuse',
];

// Full-question labels (IT-3175 uses the complete question as the field label).
const DL_MAT_3_LABEL = 'Are materials compatible with each other to ensure overall durability?';
const COMPONENT_SEPARABILITY_LABEL =
  'Are the non-textile accessories easily detachable? (zippers, buttons, interlinings)';
const DL_REP_QUESTIONS = [
  { source: 'dl_rep_1', label: 'Does the article use constructions that allow repair (accessible seams)?' },
  { source: 'dl_rep_2', label: 'Are the main components designed to be detachable without damaging the article?' },
];
const DL_STY_QUESTIONS = [
  { source: 'dl_sty_1', label: 'Does the article use timeless or classic styles?' },
  { source: 'dl_sty_2', label: 'Does it use neutral or timeless colours?' },
];
const DL_MOD_QUESTIONS = [
  { source: 'dl_mod_1', label: 'Does it incorporate adjustable elements (sizing, belts, extra buttons)?' },
  { source: 'dl_mod_2', label: 'Is it designed for multi-functionality or modular use?' },
];

// id -> name map of ArticleApplication, used to drive the CF/PR N/A rule from the FK.
const useArticleApplicationMap = () => {
  const { data } = useGetList(
    'articleApplication',
    { page: 1, perPage: 1000 },
    { field: 'name', order: 'ASC' },
    {},
  );
  const map = {};
  Object.values(data || {}).forEach((rec) => {
    if (rec && rec.id) map[rec.id] = rec.name;
  });
  return map;
};

// Cleans the Edit payload before save: drops nested relations, coerces optional
// empties to null, and forces CF/PR to null for N/A applications (see IT-3175).
const transformArticleProjectData = (data, appMap) => {
  const rest = { ...data };
  // Nested relation objects must not be sent on update (only the scalar FKs).
  ['articleApplication', 'account', 'project', 'category', 'accountArticle', 'favoriteViews']
    .forEach((key) => { delete rest[key]; });

  CIRCULARITY_NUMERIC_FIELDS.forEach((field) => {
    const value = rest[field];
    if (value === '' || value === undefined || (typeof value === 'number' && Number.isNaN(value))) {
      rest[field] = null;
    }
  });
  if (rest.market_segment === '' || rest.market_segment === undefined) rest.market_segment = null;
  if (rest.articleApplicationId === '' || rest.articleApplicationId === undefined) rest.articleApplicationId = null;

  if (isNaApplication(appMap[rest.articleApplicationId])) {
    rest.colour_fastness = null;
    rest.pilling_resistance = null;
  }
  return rest;
};

// Renders market_segment label, or the given empty text when unset.
const renderMarketSegment = (emptyText) => (record) => {
  const choice = MARKET_SEGMENT_CHOICES.find((c) => c.id === record.market_segment);
  return choice ? choice.name : emptyText;
};

// Renders a nullable boolean as Yes / No / — (keeps null distinct from false).
const renderNullableBool = (source) => (record) => {
  const value = record[source];
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '—';
};

// Show render for CF/PR: "N/A" for N/A applications, else the value or "—".
const renderCfPrShow = (source) => (record) => {
  if (isNaApplication(record.articleApplication && record.articleApplication.name)) return 'N/A';
  return record[source] != null ? record[source] : '—';
};

// Compact list summary of the 8 circularity booleans: "<yes>/<answered> yes".
const renderBooleanSummary = (record) => {
  let yes = 0;
  let answered = 0;
  CIRCULARITY_BOOLEAN_FIELDS.forEach((source) => {
    if (record[source] === true) { yes += 1; answered += 1; } else if (record[source] === false) { answered += 1; }
  });
  return answered === 0 ? '—' : `${yes}/${answered} yes`;
};

// Renders a long question as a full-width block label above its input, so the long
// circularity questions don't overflow the narrow MUI floating label (IT-3175).
const FieldQuestion = ({ label, children }) => (
  <div style={{ width: '100%', maxWidth: 600, marginTop: 12, marginBottom: 4 }}>
    <div style={{ fontSize: '0.8125rem', color: 'rgba(0, 0, 0, 0.6)', lineHeight: 1.4, marginBottom: 2 }}>
      {label}
    </div>
    {children}
  </div>
);

// Colour Fastness + Pilling Resistance inputs, disabled (N/A) for certain applications.
const CfPrInputs = ({ appMap }) => (
  <FormDataConsumer>
    {({ formData }) => {
      const na = isNaApplication(appMap[formData && formData.articleApplicationId]);
      return (
        <>
          <FieldQuestion label="Colour Fastness class (ISO 105)">
            <NumberInput
              source="colour_fastness"
              label={false}
              min={1}
              max={5}
              step={1}
              disabled={na}
              helperText={na ? 'N/A for this article application' : undefined}
            />
          </FieldQuestion>
          <FieldQuestion label="Pilling Resistance class (ISO 12945)">
            <NumberInput
              source="pilling_resistance"
              label={false}
              min={1}
              max={5}
              step={1}
              disabled={na}
              helperText={na ? 'N/A for this article application' : undefined}
            />
          </FieldQuestion>
        </>
      );
    }}
  </FormDataConsumer>
);

// ─── Article Project ──────────────────────────────────────────────────────────

const ArticleProjectFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Search by name" source="name" alwaysOn />
    <TextInput label="Reference" source="reference" />
    <TextInput label="Collection" source="collection" />
    <ReferenceInput label="Project" source="projectId" reference="projects" filterToQuery={q => ({ name: q })}>
      <SelectInput optionText="name" />
    </ReferenceInput>
  </Filter>
);

const ArticleProjectList = (props) => (
  <List
    {...props}
    filters={<ArticleProjectFilter />}
    pagination={<ProjectPagination />}
    bulkActionButtons={false}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <TextField source="id" />
      <TextField source="name" />
      <TextField source="reference" />
      <TextField source="collection" />
      <ReferenceField label="Project" source="projectId" reference="projects" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Category" source="categoryId" reference="articleCategory" link={false}>
        <TextField source="name" />
      </ReferenceField>
      {/* Circularity (IT-3175) */}
      <ReferenceField label="Article application" source="articleApplicationId" reference="articleApplication" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <FunctionField label="Market segment" render={renderMarketSegment('N/A')} />
      <FunctionField label="Durability (yes)" render={renderBooleanSummary} />
      <NumberField source="selling_price" label="Selling price (€)" emptyText="—" />
      <NumberField source="amount" />
      <NumberField source="weight" />
      <DateField source="createdAt" label="Created" />
      <EditButton />
    </Datagrid>
  </List>
);

const ArticleProjectShow = (props) => (
  <Show {...props}>
    <SimpleShowLayout>
      <TextField source="id" label="ID" />
      <TextField source="name" />
      <TextField source="reference" />
      <ImageField source="imageUrl" label="Image" />
      <TextField source="collection" />
      <ReferenceField label="Project" source="projectId" reference="projects" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Category" source="categoryId" reference="articleCategory" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <NumberField source="amount" />
      <NumberField source="weight" />
      <TextField source="color" />
      {/* Traceability */}
      <NumberField source="Traceability_Process" label="Traceability Process" />
      <NumberField source="Traceability_Km" label="Traceability Km" />
      <NumberField source="Traceability_Suppliers" label="Traceability Suppliers" />
      <NumberField source="Traceability_Certificates" label="Traceability Certificates" />
      <NumberField source="Traceability_cycle_stage" label="Traceability Cycle Stage" />
      <NumberField source="Traceability_rmat_suppliers_traced" label="Raw Mat. Suppliers Traced" />
      <NumberField source="Traceability_mat_suppliers_traced" label="Mat. Suppliers Traced" />
      <NumberField source="Traceability_article_suppliers_traced" label="Article Suppliers Traced" />
      <NumberField source="Traceability_total_suppliers_traced" label="Total Suppliers Traced" />
      <NumberField source="Traceability_score" label="Traceability Score" />
      <NumberField source="Traceability_certificates_validated_material" label="Certificates Validated Material" />
      <NumberField source="Traceability_certificates_validated_supply" label="Certificates Validated Supply" />
      <NumberField source="Traceability_cycle_stage_total" label="Cycle Stage Total" />
      {/* Manufacturing */}
      <NumberField source="Article_manufacturing_loss_rate" label="Manufacturing Loss Rate" />
      {/* Categorization (IT-3175) */}
      <SectionTitle>Categorization</SectionTitle>
      <ReferenceField label="Article application" source="articleApplicationId" reference="articleApplication" link={false}>
        <TextField source="name" />
      </ReferenceField>
      {/* Market segment (IT-3175) */}
      <SectionTitle>Market segment</SectionTitle>
      <FunctionField label="Market segment" render={renderMarketSegment('—')} />
      {/* Care & Use / Durabilidad textil (DPP - IT-3099) */}
      <SectionTitle>Care &amp; Use / Durabilidad textil</SectionTitle>
      <FunctionField label="Care instructions (ISO 3758)" render={renderCareInstructions} />
      <NumberField source="declared_service_life" label="Declared service life (years)" emptyText="—" />
      {/* DL_mat — durability of materials (IT-3175) */}
      <SectionTitle>Durability — Materials (DL_mat)</SectionTitle>
      <FunctionField label="Colour Fastness class (ISO 105)" render={renderCfPrShow('colour_fastness')} />
      <FunctionField label="Pilling Resistance class (ISO 12945)" render={renderCfPrShow('pilling_resistance')} />
      <FunctionField label={DL_MAT_3_LABEL} render={renderNullableBool('dl_mat_3')} />
      {/* DL_rep — repairability (IT-3175) */}
      <SectionTitle>Durability — Repairability (DL_rep)</SectionTitle>
      <FunctionField label={DL_REP_QUESTIONS[0].label} render={renderNullableBool('dl_rep_1')} />
      <FunctionField label={DL_REP_QUESTIONS[1].label} render={renderNullableBool('dl_rep_2')} />
      {/* DL_sty — style (IT-3175) */}
      <SectionTitle>Durability — Style (DL_sty)</SectionTitle>
      <FunctionField label={DL_STY_QUESTIONS[0].label} render={renderNullableBool('dl_sty_1')} />
      <FunctionField label={DL_STY_QUESTIONS[1].label} render={renderNullableBool('dl_sty_2')} />
      {/* DL_mod — modularity (IT-3175) */}
      <SectionTitle>Durability — Modularity (DL_mod)</SectionTitle>
      <FunctionField label={DL_MOD_QUESTIONS[0].label} render={renderNullableBool('dl_mod_1')} />
      <FunctionField label={DL_MOD_QUESTIONS[1].label} render={renderNullableBool('dl_mod_2')} />
      {/* EoL — end of life (IT-3175) */}
      <SectionTitle>End of Life (EoL)</SectionTitle>
      <FunctionField label={COMPONENT_SEPARABILITY_LABEL} render={renderNullableBool('component_separability')} />
      <NumberField source="recovery_rate" label="Recovery rate through end-of-life collectors (%)" emptyText="—" />
      <NumberField source="eol_recycling" label="End-of-life collection — Recycling (%)" emptyText="—" />
      <NumberField source="recycling_efficiency" label="Recycling efficiency (%)" emptyText="—" />
      <NumberField source="eol_reuse" label="End-of-life collection — Reuse (%)" emptyText="—" />
      {/* Selling price (IT-3175) */}
      <SectionTitle>Selling price</SectionTitle>
      <NumberField source="selling_price" label="Selling price (€)" emptyText="—" />
      <DateField source="createdAt" label="Created" />
      <DateField source="updatedAt" label="Updated" />
    </SimpleShowLayout>
  </Show>
);

const ArticleProjectEdit = (props) => {
  const appMap = useArticleApplicationMap();
  const transform = (data) => transformArticleProjectData(data, appMap);
  return (
  <Edit {...props} transform={transform}>
    <SimpleForm>
      <TextInput source="name" />
      <TextInput source="reference" />
      <TextInput source="imageUrl" label="Image URL" />
      <TextInput source="collection" />
      <ReferenceInput label="Project" source="projectId" reference="projects" filterToQuery={q => ({ name: q })}>
        <SelectInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput label="Category" source="categoryId" reference="articleCategory" filterToQuery={q => ({ name: q })}>
        <SelectInput optionText="name" />
      </ReferenceInput>
      <NumberInput source="amount" step="any" />
      <NumberInput source="weight" step="any" />
      <TextInput source="color" />
      {/* Traceability */}
      <NumberInput source="Traceability_Process" label="Traceability Process" step="any" />
      <NumberInput source="Traceability_Km" label="Traceability Km" step="any" />
      <NumberInput source="Traceability_Suppliers" label="Traceability Suppliers" step="any" />
      <NumberInput source="Traceability_Certificates" label="Traceability Certificates" step="any" />
      <NumberInput source="Traceability_cycle_stage" label="Traceability Cycle Stage" step="any" />
      <NumberInput source="Traceability_rmat_suppliers_traced" label="Raw Mat. Suppliers Traced" step="any" />
      <NumberInput source="Traceability_mat_suppliers_traced" label="Mat. Suppliers Traced" step="any" />
      <NumberInput source="Traceability_article_suppliers_traced" label="Article Suppliers Traced" step="any" />
      <NumberInput source="Traceability_total_suppliers_traced" label="Total Suppliers Traced" step="any" />
      <NumberInput source="Traceability_score" label="Traceability Score" step="any" />
      <NumberInput source="Traceability_certificates_validated_material" label="Certificates Validated Material" step="any" />
      <NumberInput source="Traceability_certificates_validated_supply" label="Certificates Validated Supply" step="any" />
      <NumberInput source="Traceability_cycle_stage_total" label="Cycle Stage Total" step="any" />
      {/* Manufacturing */}
      <NumberInput source="Article_manufacturing_loss_rate" label="Manufacturing Loss Rate" step="any" />
      {/* Categorization (IT-3175) */}
      <SectionTitle>Categorization</SectionTitle>
      <ReferenceInput label="Article application" source="articleApplicationId" reference="articleApplication" perPage={100} filterToQuery={q => ({ 'name||$contL': q })}>
        <SelectInput optionText="name" allowEmpty />
      </ReferenceInput>
      {/* Market segment (IT-3175) */}
      <SectionTitle>Market segment</SectionTitle>
      <SelectInput source="market_segment" label="Market segment" choices={MARKET_SEGMENT_CHOICES} allowEmpty />
      {/* Care & Use / Durabilidad textil (DPP - IT-3099) */}
      <SectionTitle>Care &amp; Use / Durabilidad textil</SectionTitle>
      <CareInstructionsInput source="care_instructions" label="Care instructions (ISO 3758)" />
      <NumberInput source="declared_service_life" label="Declared service life (years)" min={0} step="any" />
      {/* DL_mat — durability of materials (IT-3175) */}
      <SectionTitle>Durability — Materials (DL_mat)</SectionTitle>
      <CfPrInputs appMap={appMap} />
      <FieldQuestion label={DL_MAT_3_LABEL}>
        <NullableBooleanInput source="dl_mat_3" label={false} />
      </FieldQuestion>
      {/* DL_rep — repairability (IT-3175) */}
      <SectionTitle>Durability — Repairability (DL_rep)</SectionTitle>
      <FieldQuestion label={DL_REP_QUESTIONS[0].label}>
        <NullableBooleanInput source="dl_rep_1" label={false} />
      </FieldQuestion>
      <FieldQuestion label={DL_REP_QUESTIONS[1].label}>
        <NullableBooleanInput source="dl_rep_2" label={false} />
      </FieldQuestion>
      {/* DL_sty — style (IT-3175) */}
      <SectionTitle>Durability — Style (DL_sty)</SectionTitle>
      <FieldQuestion label={DL_STY_QUESTIONS[0].label}>
        <NullableBooleanInput source="dl_sty_1" label={false} />
      </FieldQuestion>
      <FieldQuestion label={DL_STY_QUESTIONS[1].label}>
        <NullableBooleanInput source="dl_sty_2" label={false} />
      </FieldQuestion>
      {/* DL_mod — modularity (IT-3175) */}
      <SectionTitle>Durability — Modularity (DL_mod)</SectionTitle>
      <FieldQuestion label={DL_MOD_QUESTIONS[0].label}>
        <NullableBooleanInput source="dl_mod_1" label={false} />
      </FieldQuestion>
      <FieldQuestion label={DL_MOD_QUESTIONS[1].label}>
        <NullableBooleanInput source="dl_mod_2" label={false} />
      </FieldQuestion>
      {/* EoL — end of life (IT-3175) */}
      <SectionTitle>End of Life (EoL)</SectionTitle>
      <FieldQuestion label={COMPONENT_SEPARABILITY_LABEL}>
        <NullableBooleanInput source="component_separability" label={false} />
      </FieldQuestion>
      <FieldQuestion label="Recovery rate through end-of-life collectors (%)">
        <NumberInput source="recovery_rate" label={false} min={0} max={100} step="any" />
      </FieldQuestion>
      <FieldQuestion label="End-of-life collection — Recycling (%)">
        <NumberInput source="eol_recycling" label={false} min={0} max={100} step="any" />
      </FieldQuestion>
      <FieldQuestion label="Recycling efficiency (%)">
        <NumberInput source="recycling_efficiency" label={false} min={0} max={100} step="any" />
      </FieldQuestion>
      <FieldQuestion label="End-of-life collection — Reuse (%)">
        <NumberInput source="eol_reuse" label={false} min={0} max={100} step="any" />
      </FieldQuestion>
      {/* Selling price (IT-3175) */}
      <SectionTitle>Selling price</SectionTitle>
      <NumberInput source="selling_price" label="Selling price (€)" min={0} step="any" />
    </SimpleForm>
  </Edit>
  );
};

// ─── Material Project ─────────────────────────────────────────────────────────

const MaterialProjectFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Search by name" source="materialName" alwaysOn />
    <ReferenceInput label="Project" source="projectId" reference="projects" filterToQuery={q => ({ name: q })}>
      <SelectInput optionText="name" />
    </ReferenceInput>
  </Filter>
);

const MaterialProjectList = (props) => (
  <List
    {...props}
    filters={<MaterialProjectFilter />}
    pagination={<ProjectPagination />}
    bulkActionButtons={false}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <TextField source="id" />
      <TextField source="materialName" label="Name" />
      <ReferenceField label="Project" source="projectId" reference="projects" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Category" source="categoryId" reference="materialsCategory" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <TextField source="material_reference" label="Reference" />
      <FunctionField
        label="Loss Rate"
        source="Material_processing_loss_rate"
        render={(record) =>
          record.Material_processing_loss_rate != null
            ? record.Material_processing_loss_rate
            : "not declared"
        }
      />
      <DateField source="createdAt" label="Created" />
      <EditButton />
    </Datagrid>
  </List>
);

const MaterialProjectShow = (props) => (
  <Show {...props}>
    <SimpleShowLayout>
      <TextField source="id" label="ID" />
      <TextField source="name" label="Name" />
      <ReferenceField label="Project" source="projectId" reference="projects" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Category" source="categoryId" reference="materialsCategory" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Application" source="applicationId" reference="materialsApplication" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <TextField source="comments" />
      <TextField source="material_reference" label="Reference" />
      <TextField source="certificate_declared" label="Certificate Declared" />
      <FunctionField
        label="Loss Rate"
        source="Material_processing_loss_rate"
        render={(record) =>
          record.Material_processing_loss_rate != null
            ? record.Material_processing_loss_rate
            : "not declared"
        }
      />
      <DateField source="createdAt" label="Created" />
      <DateField source="updatedAt" label="Updated" />
    </SimpleShowLayout>
  </Show>
);

const MaterialProjectEdit = (props) => (
  <Edit {...props}>
    <SimpleForm>
      <ReferenceInput label="Project" source="projectId" reference="projects" filterToQuery={q => ({ name: q })}>
        <SelectInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput label="Category" source="categoryId" reference="materialsCategory" filterToQuery={q => ({ name: q })}>
        <SelectInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput label="Application" source="applicationId" reference="materialsApplication" filterToQuery={q => ({ name: q })}>
        <SelectInput optionText="name" />
      </ReferenceInput>
      <TextInput source="comments" multiline />
      <TextInput source="material_reference" label="Reference" />
      <TextInput source="certificate_declared" label="Certificate Declared" />
      <NumberInput source="lossRateApplied" label="Loss Rate Applied" step="any" />
      <NumberInput source="lossRateManufactureApplied" label="Loss Rate Manufacture Applied" step="any" />
      <NumberInput source="Material_processing_loss_rate" label="Processing Loss Rate" step="any" />
    </SimpleForm>
  </Edit>
);

// ─── Supplier Project ─────────────────────────────────────────────────────────

const SupplierProjectFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Search by name" source="name" alwaysOn />
    <ReferenceInput label="Project" source="projectId" reference="projects" filterToQuery={q => ({ name: q })}>
      <SelectInput optionText="name" />
    </ReferenceInput>
  </Filter>
);

const SupplierProjectList = (props) => (
  <List
    {...props}
    filters={<SupplierProjectFilter />}
    pagination={<ProjectPagination />}
    bulkActionButtons={false}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <TextField source="id" />
      <TextField source="supplierName" label="Name" />
      <ReferenceField label="Project" source="projectId" reference="projects" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <ReferenceArrayField label="Category" source="categoryId" reference="lcaCategory">
        <SingleFieldList linkType={false}>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>
      <ReferenceField label="Origin" source="originId" reference="origins" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <NumberField source="total_certificate" label="Total Certificates" />
      <FunctionField source="percentagePhotovoltaic" label="% Photovoltaic" render={(record) => percentageFormat(record.percentagePhotovoltaic)} />
      <FunctionField source="percentageWind" label="% Wind" render={(record) => percentageFormat(record.percentageWind)} />
      <FunctionField source="percentageHydro" label="% Hydro" render={(record) => percentageFormat(record.percentageHydro)} />
      <UrlField
        source="picture_url"
        label="Picture URL (for DPP)"
        emptyText="—"
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      />
      <DateField source="createdAt" label="Created" />
      <EditButton />
    </Datagrid>
  </List>
);

const SupplierProjectShow = (props) => (
  <Show {...props}>
    <SimpleShowLayout>
      <TextField source="id" label="ID" />
      <TextField source="name" label="Name" />
      <ReferenceField label="Project" source="projectId" reference="projects" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <ReferenceArrayField label="Category" source="categoryId" reference="lcaCategory">
        <SingleFieldList linkType={false}>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>
      <ReferenceField label="Origin" source="originId" reference="origins" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Transport" source="transportId" reference="transport" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <TextField source="comments" />
      <NumberField source="total_certificate" label="Total Certificates" />
      <FunctionField source="percentagePhotovoltaic" label="% Photovoltaic" render={(record) => percentageFormat(record.percentagePhotovoltaic)} />
      <FunctionField source="percentageWind" label="% Wind" render={(record) => percentageFormat(record.percentageWind)} />
      <FunctionField source="percentageHydro" label="% Hydro" render={(record) => percentageFormat(record.percentageHydro)} />
      <TextField source="useRenewableEnergy" label="Renewable Energy" />
      <TextField source="closedLoopWaterSystem" label="Closed Loop Water System" />
      <TextField source="artisanalManufacture" label="Artisanal Manufacture" />
      <TextField source="fairtradeTrader" label="Fairtrade Trader" />
      <TextField source="labourInsertionPrograms" label="Labour Insertion Programs" />
      <TextField source="reduced" label="Reduced" />
      <TextField source="minimisationOfWaste" label="Minimisation Of Waste" />
      <TextField source="certificate_declared" label="Certificate Declared" />
      <DateField source="createdAt" label="Created" />
      <DateField source="updatedAt" label="Updated" />
    </SimpleShowLayout>
  </Show>
);

const SupplierProjectCreate = (props) => (
  <Create {...props}>
    <SimpleForm>
      <ReferenceInput label="Project" source="projectId" reference="projects" filterToQuery={q => ({ name: q })}>
        <SelectInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput label="Category" source="categoryId" reference="lcaCategory" filterToQuery={q => ({ name: q })}>
        <SelectInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={q => ({ name: q })}>
        <SelectInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput label="Transport" source="transportId" reference="transport" filterToQuery={q => ({ name: q })}>
        <SelectInput optionText="name" />
      </ReferenceInput>
      <TextInput source="comments" multiline />
      <NumberInput source="total_certificate" label="Total Certificates" step="any" />
      <TextInput source="percentagePhotovoltaic" label="% Photovoltaic" validate={validatePercentage} />
      <TextInput source="percentageWind" label="% Wind" validate={validatePercentage} />
      <TextInput source="percentageHydro" label="% Hydro" validate={validatePercentage} />
      <SelectInput source="useRenewableEnergy" label="Renewable Energy" choices={YES_NO_CHOICES} />
      <SelectInput source="closedLoopWaterSystem" label="Closed Loop Water System" choices={YES_NO_CHOICES} />
      <SelectInput source="artisanalManufacture" label="Artisanal Manufacture" choices={YES_NO_CHOICES} />
      <SelectInput source="fairtradeTrader" label="Fairtrade Trader" choices={YES_NO_CHOICES} />
      <SelectInput source="labourInsertionPrograms" label="Labour Insertion Programs" choices={YES_NO_CHOICES} />
      <SelectInput source="reduced" label="Reduced" choices={YES_NO_CHOICES} />
      <SelectInput source="minimisationOfWaste" label="Minimisation Of Waste" choices={YES_NO_CHOICES} />
      <TextInput source="certificate_declared" label="Certificate Declared" />
    </SimpleForm>
  </Create>
);

const SupplierProjectEdit = (props) => (
  <Edit {...props}>
    <SimpleForm>
      <ReferenceInput label="Project" source="projectId" reference="projects" filterToQuery={q => ({ name: q })}>
        <SelectInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput label="Category" source="categoryId" reference="lcaCategory" filterToQuery={q => ({ name: q })}>
        <SelectInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={q => ({ name: q })}>
        <SelectInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput label="Transport" source="transportId" reference="transport" filterToQuery={q => ({ name: q })}>
        <SelectInput optionText="name" />
      </ReferenceInput>
      <TextInput source="comments" multiline />
      <NumberInput source="total_certificate" label="Total Certificates" step="any" />
      <TextInput source="percentagePhotovoltaic" label="% Photovoltaic" validate={validatePercentage} />
      <TextInput source="percentageWind" label="% Wind" validate={validatePercentage} />
      <TextInput source="percentageHydro" label="% Hydro" validate={validatePercentage} />
      <SelectInput source="useRenewableEnergy" label="Renewable Energy" choices={YES_NO_CHOICES} />
      <SelectInput source="closedLoopWaterSystem" label="Closed Loop Water System" choices={YES_NO_CHOICES} />
      <SelectInput source="artisanalManufacture" label="Artisanal Manufacture" choices={YES_NO_CHOICES} />
      <SelectInput source="fairtradeTrader" label="Fairtrade Trader" choices={YES_NO_CHOICES} />
      <SelectInput source="labourInsertionPrograms" label="Labour Insertion Programs" choices={YES_NO_CHOICES} />
      <SelectInput source="reduced" label="Reduced" choices={YES_NO_CHOICES} />
      <SelectInput source="minimisationOfWaste" label="Minimisation Of Waste" choices={YES_NO_CHOICES} />
      <TextInput source="certificate_declared" label="Certificate Declared" />
      <TextInput
        source="picture_url"
        label="Picture URL (for DPP)"
        type="url"
        fullWidth
        parse={(value) => (value === '' ? null : value)}
      />
    </SimpleForm>
  </Edit>
);

// ─── Retail Packaging Project ─────────────────────────────────────────────────

const RetailPackagingProjectFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Search by name" source="name" alwaysOn />
    <ReferenceInput label="Project" source="projectId" reference="projects" filterToQuery={q => ({ name: q })}>
      <SelectInput optionText="name" />
    </ReferenceInput>
  </Filter>
);

const RetailPackagingProjectList = (props) => (
  <List
    {...props}
    filters={<RetailPackagingProjectFilter />}
    pagination={<ProjectPagination />}
    bulkActionButtons={false}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <TextField source="id" />
      <TextField source="name" />
      <ReferenceField label="Project" source="projectId" reference="projects" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <TextField source="supplier.name" label="Supplier" />
      <BooleanField source="takeBack" label="Take Back" />
      <DateField source="createdAt" label="Created" />
      <EditButton />
    </Datagrid>
  </List>
);

const RetailPackagingProjectShow = (props) => (
  <Show {...props}>
    <SimpleShowLayout>
      <TextField source="id" label="ID" />
      <TextField source="name" />
      <ReferenceField label="Project" source="projectId" reference="projects" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Packaging" source="packagingId" reference="packaging" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <TextField source="supplier.name" label="Supplier" />
      <BooleanField source="takeBack" label="Take Back" />
      <ReferenceField label="Hazardous Substances" source="hazardousSubstancesId" reference="hazardousSubstances" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Process" source="processId" reference="packaging" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <DateField source="createdAt" label="Created" />
      <DateField source="updatedAt" label="Updated" />
    </SimpleShowLayout>
  </Show>
);

const RetailPackagingProjectEdit = (props) => (
  <Edit {...props}>
    <SimpleForm>
      <TextInput source="name" />
      <ReferenceInput label="Project" source="projectId" reference="projects" filterToQuery={q => ({ name: q })}>
        <SelectInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput label="Packaging" source="packagingId" reference="packaging" filterToQuery={q => ({ name: q })}>
        <SelectInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput label="Supplier" source="supplierId" reference="supplier" filterToQuery={q => ({ name: q })}>
        <SelectInput optionText="name" />
      </ReferenceInput>
      <BooleanInput source="takeBack" label="Take Back" defaultValue={false} />
      <ReferenceInput label="Hazardous Substances" source="hazardousSubstancesId" reference="hazardousSubstances" filterToQuery={q => ({ name: q })}>
        <SelectInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput label="Process" source="processId" reference="packaging" filterToQuery={q => ({ name: q })}>
        <SelectInput optionText="name" />
      </ReferenceInput>
    </SimpleForm>
  </Edit>
);

// ─── Resource exports ─────────────────────────────────────────────────────────
// Resource names use camelCase. The dataProvider maps them to the real API paths.

export const articleProjectResource = () => (
  <Resource
    name="articleProject"
    options={{ label: "Article Project", menuGroup: "Articles" }}
    list={ArticleProjectList}
    show={ArticleProjectShow}
    edit={ArticleProjectEdit}
  />
);

// Article Application (IT-3180): full CRUD reference table holding the Design
// Longevity weights (w_mat/w_rep/w_sty/w_mod) and the CF/PR default classes per
// application type. Also backs the article_application <ReferenceInput> in
// Article Project (IT-3175). Fields match the BE ArticleApplication entity
// (IT-3131): the ticket's utility_cap/design_longevity_weight do not exist —
// utility_cap was dropped and the DL weight is the four w_* fields.
// The articleApplication endpoint is a plain @nestjsx/crud controller: it rejects
// the $like operator that composeFilter generates for text searches, so the source
// forces $contL (the nestjsx/crud case-insensitive "contains" operator) explicitly.
const ArticleApplicationFilters = (props) => (
  <Filter {...props}>
    <TextInput label="Search by name" source="name||$contL" alwaysOn resettable />
  </Filter>
);

// Delete confirm dialog shows the application name instead of the raw uuid.
const ArticleApplicationDeleteButton = ({ record, ...props }) => (
  <DeleteButton
    {...props}
    record={record}
    undoable={false}
    confirmTitle={`Delete Article Application "${(record && record.name) || ''}"`}
  />
);

const ArticleApplicationList = (props) => (
  <List {...props} filters={<ArticleApplicationFilters />} pagination={<ProjectPagination />}>
    <div style={{ marginTop: "15px" }}>
      <Datagrid rowClick="edit">
        <TextField source="name" />
        <NumberField source="w_mat" label="Materials weight (w_mat)" />
        <NumberField source="w_rep" label="Repairability weight (w_rep)" />
        <NumberField source="w_sty" label="Style weight (w_sty)" />
        <NumberField source="w_mod" label="Modularity weight (w_mod)" />
        <NumberField source="cf_default_class" label="Colour Fastness default class" />
        <NumberField source="pr_default_class" label="Pilling Resistance default class" />
        <TextField source="justification" label="Justification" emptyText="-" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <EditButton />
        <ArticleApplicationDeleteButton />
      </Datagrid>
    </div>
  </List>
);

const ArticleApplicationForm = (props) => (
  <SimpleForm {...props}>
    <TextInput source="name" validate={required()} />
    <NumberInput source="w_mat" label="Materials weight (w_mat)" step="any" validate={[required(), number()]} />
    <NumberInput source="w_rep" label="Repairability weight (w_rep)" step="any" validate={[required(), number()]} />
    <NumberInput source="w_sty" label="Style weight (w_sty)" step="any" validate={[required(), number()]} />
    <NumberInput source="w_mod" label="Modularity weight (w_mod)" step="any" validate={[required(), number()]} />
    <NumberInput source="cf_default_class" label="Colour Fastness default class" step={1} validate={[number(), minValue(1), maxValue(5)]} />
    <NumberInput source="pr_default_class" label="Pilling Resistance default class" step={1} validate={[number(), minValue(1), maxValue(5)]} />
    <TextInput source="justification" label="Justification" multiline fullWidth />
  </SimpleForm>
);

const ArticleApplicationCreate = (props) => (
  <Create {...props}>
    <ArticleApplicationForm />
  </Create>
);

const ArticleApplicationEdit = (props) => (
  <Edit {...props}>
    <ArticleApplicationForm />
  </Edit>
);

export const articleApplicationResource = () => (
  <Resource
    name="articleApplication"
    options={{ label: "Article Application", menuGroup: "Bcome" }}
    list={ArticleApplicationList}
    create={ArticleApplicationCreate}
    edit={ArticleApplicationEdit}
  />
);

export const materialProjectResource = () => (
  <Resource
    name="materialProject"
    options={{ label: "Material Project", menuGroup: "Materials" }}
    list={MaterialProjectList}
    show={MaterialProjectShow}
    edit={MaterialProjectEdit}
  />
);

export const supplierProjectResource = () => (
  <Resource
    name="supplierProject"
    options={{ label: "Supplier Project", menuGroup: "Suppliers" }}
    list={SupplierProjectList}
    create={SupplierProjectCreate}
    show={SupplierProjectShow}
    edit={SupplierProjectEdit}
  />
);

export const retailPackagingProjectResource = () => (
  <Resource
    name="retailPackagingProject"
    options={{ label: "Retail Packaging Project", menuGroup: "Packaging" }}
    list={RetailPackagingProjectList}
    show={RetailPackagingProjectShow}
    edit={RetailPackagingProjectEdit}
  />
);
